import { strIsNullOrEmpty } from '@fox/atoms/utils/string';
import { Dispatch, SetStateAction } from 'react';
import { ISection } from '../blocks/section-block/contracts';

/**
 * Navigates to a specific page or section in a form.
 * This function is used to navigate to a specific form page or section, typically in response to interacting with form validation errors.
 * @param nextSection - The target page/section number to navigate to.
 * @param currentPage - The current page number of the form.
 * @param setCurrentPage - Function to update the current page state.
 * @param setPrevPage - Function to update the history of previously visited pages
 * @returns void
 */
export const goToNextFormPage = (
	nextSection: number,
	currentPage: number,
	setCurrentPage: Dispatch<SetStateAction<number>>,
	setPrevPage: React.Dispatch<React.SetStateAction<number[]>>
) => {
	if (nextSection === currentPage) return;
	setPrevPage?.(prev => {
		const newArr = [...prev];
		const index = newArr.indexOf(nextSection);

		// If index is found it means we are trying to go to a previous page we have visited already and will remove sections from the prevPage array to get back to that point.
		if (index >= 0) {
			newArr.splice(index);
			return newArr;
		} else {
			return [...newArr, currentPage];
		}
	});
	setCurrentPage?.(nextSection);
};

export function buildTransitionMap(sections: ISection[]) {
	const maps = [] as { prev: number | null; next: number }[];
	for (let i = 0; i < sections.length; i++) {
		const s = sections[i];
		const item: { prev: number | null; next: number } = { prev: null, next: i + 1 };
		maps.push(item);
		if (i > 0) {
			//Default prev to the previous page in sequence
			item.prev = i - 1;
		}
		//If there is a custom next section id, set its index as the next one
		if (!strIsNullOrEmpty(s.nextSectionId)) {
			item.next = sections.findIndex(sct => sct.branchId == s.nextSectionId);
		}
	}
	//Now we iterate again, to fix up the prev instances for where there is a change of default next page
	for (let i = 0; i < maps.length; i++) {
		const m = maps[i];
		// This page will advance to a page beyond the next in sequence
		if (m.next > i + 1) {
			// Fix up its prev to point to the currently iterated index
			const nextPage = maps[m.next];
			nextPage.prev = i;
		}
	}
	return maps;
}
